import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import {
  Building2,
  Phone,
  Mail,
  MapPin,
  Plus,
  Edit2,
  Trash2,
  Search,
  Badge,
  Clock,
  Euro,
  FileCheck,
  FileText,
  FolderOpen,
  Upload,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  X,
  ChevronRight,
  Download,
  Eye
} from 'lucide-react';

const API_URL = (window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL) || '';

// Labels pour les types de documents
const DOC_TYPE_LABELS = {
  contrat: 'Contrat de sous-traitance',
  kbis: 'Extrait Kbis',
  attestation_assurance_rc: 'Attestation RC Pro',
  attestation_assurance_decennale: 'Attestation Décennale',
  agrement_cnaps: 'Agrément CNAPS',
  carte_professionnelle: 'Carte professionnelle',
  urssaf: 'Attestation URSSAF',
  attestation_vigilance: 'Attestation de vigilance',
  rib: 'RIB',
  certificat_qualification: 'Certificat de qualification',
  habilitation_ssiap: 'Habilitation SSIAP',
  autre: 'Autre document'
};

const Subcontractors = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [subcontractors, setSubcontractors] = useState([]);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('liste');
  
  // Modals
  const [showModal, setShowModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showDocsListModal, setShowDocsListModal] = useState(false);
  
  const [editingSubcontractor, setEditingSubcontractor] = useState(null);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState(null);
  const [documents, setDocuments] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    siret: '',
    email: '',
    phone: '',
    address: '',
    tarifHoraire: '',
    numeroAgrement: '',
    specialites: [],
    contrat: '',
    notes: ''
  });
  
  const [docFormData, setDocFormData] = useState({
    type: 'contrat',
    nom: '',
    description: '',
    fichier_url: '',
    date_emission: '',
    date_expiration: ''
  });

  const specialiteOptions = [
    'Surveillance',
    'SSIAP 1',
    'SSIAP 2',
    'SSIAP 3',
    'Cynophile',
    'Événementiel',
    'Transport de fonds',
    'Télésurveillance',
    'Intervention'
  ];

  useEffect(() => {
    if (token) {
      loadSubcontractors();
    }
  }, [token]);

  const loadSubcontractors = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/subcontractors`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubcontractors(data);
      }
    } catch (error) {
      console.error('Erreur chargement sous-traitants:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async (subId) => {
    try {
      const response = await fetch(`${API_URL}/api/subcontractors/${subId}/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Erreur chargement documents:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingSubcontractor 
        ? `${API_URL}/api/subcontractors/${editingSubcontractor.id}`
        : `${API_URL}/api/subcontractors`;
      
      const method = editingSubcontractor ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          tarifHoraire: parseFloat(formData.tarifHoraire) || 0
        })
      });
      
      if (response.ok) {
        toast({
          title: editingSubcontractor ? 'Sous-traitant modifié' : 'Sous-traitant créé',
          description: 'Les informations ont été enregistrées avec succès.'
        });
        setShowModal(false);
        resetForm();
        loadSubcontractors();
      } else {
        const error = await response.json();
        toast({
          title: 'Erreur',
          description: error.detail || 'Une erreur est survenue',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder le sous-traitant',
        variant: 'destructive'
      });
    }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();
    if (!selectedSubcontractor) return;
    
    try {
      const response = await fetch(`${API_URL}/api/subcontractors/${selectedSubcontractor.id}/documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(docFormData)
      });
      
      if (response.ok) {
        toast({ title: 'Succès', description: 'Document ajouté' });
        setShowDocModal(false);
        setDocFormData({ type: 'contrat', nom: '', description: '', fichier_url: '', date_emission: '', date_expiration: '' });
        loadDocuments(selectedSubcontractor.id);
        loadSubcontractors();
      }
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible d\'ajouter le document', variant: 'destructive' });
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!selectedSubcontractor || !window.confirm('Supprimer ce document ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/subcontractors/${selectedSubcontractor.id}/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: 'Succès', description: 'Document supprimé' });
        loadDocuments(selectedSubcontractor.id);
        loadSubcontractors();
      }
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de supprimer', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir désactiver ce sous-traitant ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/subcontractors/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: 'Sous-traitant désactivé' });
        loadSubcontractors();
      }
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const openEditModal = (subcontractor) => {
    setEditingSubcontractor(subcontractor);
    setFormData({
      name: subcontractor.name,
      companyName: subcontractor.companyName,
      siret: subcontractor.siret,
      email: subcontractor.email,
      phone: subcontractor.phone,
      address: subcontractor.address,
      tarifHoraire: subcontractor.tarifHoraire.toString(),
      numeroAgrement: subcontractor.numeroAgrement || '',
      specialites: subcontractor.specialites || [],
      contrat: subcontractor.contrat || '',
      notes: subcontractor.notes || ''
    });
    setShowModal(true);
  };

  const openDocsModal = (sub) => {
    setSelectedSubcontractor(sub);
    loadDocuments(sub.id);
    setShowDocsListModal(true);
  };

  const resetForm = () => {
    setEditingSubcontractor(null);
    setFormData({
      name: '', companyName: '', siret: '', email: '', phone: '', address: '',
      tarifHoraire: '', numeroAgrement: '', specialites: [], contrat: '', notes: ''
    });
  };

  const toggleSpecialite = (spec) => {
    setFormData(prev => ({
      ...prev,
      specialites: prev.specialites.includes(spec)
        ? prev.specialites.filter(s => s !== spec)
        : [...prev.specialites, spec]
    }));
  };

  const filteredSubcontractors = subcontractors.filter(s => {
    const searchLower = search.toLowerCase();
    return (
      s.name.toLowerCase().includes(searchLower) ||
      s.companyName.toLowerCase().includes(searchLower) ||
      s.siret.includes(searchLower)
    );
  });

  const activeSubcontractors = filteredSubcontractors.filter(s => s.active);
  const docsExpiresSoon = subcontractors.filter(s => !s.documentsAJour).length;

  const getDocStatusBadge = (statut) => {
    if (statut === 'expire') return <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Expiré</span>;
    if (statut === 'a_renouveler') return <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">À renouveler</span>;
    return <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Valide</span>;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🤝 Sous-traitants</h1>
          <p className="text-gray-500">Gestion des partenaires et leurs documents</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Nouveau Sous-traitant</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold">{subcontractors.length}</p>
            </div>
            <Building2 className="w-8 h-8 text-purple-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Actifs</p>
              <p className="text-2xl font-bold text-green-600">{activeSubcontractors.length}</p>
            </div>
            <CheckCircle2 className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Docs à jour</p>
              <p className="text-2xl font-bold text-blue-600">
                {subcontractors.filter(s => s.documentsAJour !== false).length}
              </p>
            </div>
            <FileCheck className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Docs expirés</p>
              <p className="text-2xl font-bold text-red-600">{docsExpiresSoon}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Heures Totales</p>
              <p className="text-2xl font-bold">
                {subcontractors.reduce((acc, s) => acc + (s.totalHeuresTravaillees || 0), 0).toFixed(0)}h
              </p>
            </div>
            <Clock className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Alerte documents expirés */}
      {docsExpiresSoon > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="font-semibold text-red-700">
              {docsExpiresSoon} sous-traitant(s) avec documents expirés ou à renouveler
            </span>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder="Rechercher par nom, société ou SIRET..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg"
        />
      </div>

      {/* Liste des sous-traitants */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {activeSubcontractors.length === 0 ? (
          <div className="col-span-full bg-white rounded-xl p-8 text-center text-gray-500">
            Aucun sous-traitant actif trouvé
          </div>
        ) : (
          activeSubcontractors.map(sub => (
            <div key={sub.id} className={`bg-white rounded-xl p-4 shadow-sm border-l-4 ${
              sub.documentsAJour === false ? 'border-red-500' : 'border-purple-500'
            }`}>
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-gray-900">{sub.name}</h3>
                  <p className="text-sm text-purple-600 font-medium">{sub.companyName}</p>
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => openDocsModal(sub)}
                    className="p-1 text-gray-400 hover:text-blue-600"
                    title="Documents"
                  >
                    <FolderOpen className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => openEditModal(sub)}
                    className="p-1 text-gray-400 hover:text-blue-600"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(sub.id)}
                    className="p-1 text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex items-center text-gray-600">
                  <Badge className="w-4 h-4 mr-2 text-gray-400" />
                  SIRET: {sub.siret}
                </div>
                {sub.numeroAgrement && (
                  <div className="flex items-center text-green-600">
                    <FileCheck className="w-4 h-4 mr-2" />
                    CNAPS: {sub.numeroAgrement}
                  </div>
                )}
                <div className="flex items-center text-gray-600">
                  <Mail className="w-4 h-4 mr-2 text-gray-400" />
                  {sub.email}
                </div>
                <div className="flex items-center text-gray-600">
                  <Euro className="w-4 h-4 mr-2 text-gray-400" />
                  {sub.tarifHoraire}€/h
                </div>
              </div>

              {/* Indicateur documents */}
              <div className="mt-3 pt-3 border-t">
                <button
                  onClick={() => openDocsModal(sub)}
                  className={`w-full flex items-center justify-between p-2 rounded-lg ${
                    sub.documentsAJour === false 
                      ? 'bg-red-50 text-red-700' 
                      : 'bg-gray-50 text-gray-600'
                  }`}
                >
                  <div className="flex items-center">
                    <FolderOpen className="w-4 h-4 mr-2" />
                    <span className="text-sm">
                      {sub.documents?.length || 0} document(s)
                    </span>
                  </div>
                  {sub.documentsAJour === false && (
                    <AlertTriangle className="w-4 h-4" />
                  )}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {sub.specialites?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {sub.specialites.slice(0, 3).map((spec, i) => (
                    <span key={i} className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                      {spec}
                    </span>
                  ))}
                  {sub.specialites.length > 3 && (
                    <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded">
                      +{sub.specialites.length - 3}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Modal Création/Édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">
                  {editingSubcontractor ? 'Modifier le sous-traitant' : 'Nouveau sous-traitant'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom du contact *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Raison sociale *</label>
                  <input
                    type="text"
                    required
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SIRET *</label>
                  <input
                    type="text"
                    required
                    value={formData.siret}
                    onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">N° Agrément CNAPS</label>
                  <input
                    type="text"
                    value={formData.numeroAgrement}
                    onChange={(e) => setFormData({ ...formData, numeroAgrement: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse *</label>
                <input
                  type="text"
                  required
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tarif horaire (€) *</label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    value={formData.tarifHoraire}
                    onChange={(e) => setFormData({ ...formData, tarifHoraire: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de contrat</label>
                  <select
                    value={formData.contrat}
                    onChange={(e) => setFormData({ ...formData, contrat: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Sélectionner...</option>
                    <option value="ponctuel">Ponctuel</option>
                    <option value="cadre">Contrat cadre</option>
                    <option value="exclusif">Exclusif</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Spécialités</label>
                <div className="flex flex-wrap gap-2">
                  {specialiteOptions.map(spec => (
                    <button
                      key={spec}
                      type="button"
                      onClick={() => toggleSpecialite(spec)}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${
                        formData.specialites.includes(spec)
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {spec}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600">
                  Annuler
                </button>
                <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                  {editingSubcontractor ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Liste Documents */}
      {showDocsListModal && selectedSubcontractor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold">📁 Dossier Documents</h2>
                  <p className="text-gray-500">{selectedSubcontractor.companyName}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => { setShowDocModal(true); }}
                    className="px-3 py-1 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Ajouter
                  </button>
                  <button onClick={() => setShowDocsListModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FolderOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Aucun document</p>
                  <button
                    onClick={() => setShowDocModal(true)}
                    className="mt-3 text-purple-600 hover:text-purple-800"
                  >
                    + Ajouter un document
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map(doc => (
                    <div key={doc.id} className={`p-4 rounded-lg border-l-4 ${
                      doc.statut === 'expire' ? 'border-red-500 bg-red-50' :
                      doc.statut === 'a_renouveler' ? 'border-orange-500 bg-orange-50' :
                      'border-green-500 bg-green-50'
                    }`}>
                      <div className="flex justify-between items-start">
                        <div className="flex items-start space-x-3">
                          <FileText className="w-5 h-5 text-gray-500 mt-0.5" />
                          <div>
                            <p className="font-medium">{doc.nom}</p>
                            <p className="text-sm text-gray-500">{DOC_TYPE_LABELS[doc.type] || doc.type}</p>
                            {doc.description && <p className="text-sm text-gray-400 mt-1">{doc.description}</p>}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getDocStatusBadge(doc.statut)}
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="p-1 text-gray-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                        {doc.date_emission && (
                          <span className="flex items-center">
                            <Calendar className="w-3 h-3 mr-1" />
                            Émis: {doc.date_emission}
                          </span>
                        )}
                        {doc.date_expiration && (
                          <span className={`flex items-center ${
                            doc.statut === 'expire' ? 'text-red-600 font-medium' : ''
                          }`}>
                            <Calendar className="w-3 h-3 mr-1" />
                            Expire: {doc.date_expiration}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajout Document */}
      {showDocModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Ajouter un Document</h2>
                <button onClick={() => setShowDocModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleAddDocument} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de document *</label>
                <select
                  value={docFormData.type}
                  onChange={(e) => setDocFormData({ ...docFormData, type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  {Object.entries(DOC_TYPE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du document *</label>
                <input
                  type="text"
                  value={docFormData.nom}
                  onChange={(e) => setDocFormData({ ...docFormData, nom: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Ex: Contrat 2024"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date d'émission</label>
                  <input
                    type="date"
                    value={docFormData.date_emission}
                    onChange={(e) => setDocFormData({ ...docFormData, date_emission: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date d'expiration</label>
                  <input
                    type="date"
                    value={docFormData.date_expiration}
                    onChange={(e) => setDocFormData({ ...docFormData, date_expiration: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={docFormData.description}
                  onChange={(e) => setDocFormData({ ...docFormData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL du fichier (optionnel)</label>
                <input
                  type="url"
                  value={docFormData.fichier_url}
                  onChange={(e) => setDocFormData({ ...docFormData, fichier_url: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="https://..."
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button type="button" onClick={() => setShowDocModal(false)} className="px-4 py-2 text-gray-600">
                  Annuler
                </button>
                <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subcontractors;
